import { X } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { ScheduleStore } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription, AlertTitle } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';

import { useNotAddedStore } from '../lib/not-added-store';

import { MemberResultDetail } from './member-result-detail';

/** Rows listed before "and N more". */
const LISTED = 20;

/**
 * After a create whose later add chunks left products out (an owner elsewhere, a taken handle,
 * a whole chunk refused): which products are not in the pool and why. The create page hands the
 * rows over through `useNotAddedStore`; dismissing forgets them.
 */
export function NotAddedAlert({
  scheduleId,
  stores,
}: {
  scheduleId: string;
  stores: readonly ScheduleStore[];
}) {
  const { t } = useTranslation(['schedules', 'errors']);
  const notAdded = useNotAddedStore((state) => state.bySchedule[scheduleId]);
  const clear = useNotAddedStore((state) => state.clear);
  const storeNames = useMemo(() => new Map(stores.map((s) => [s.storeId, s.storeName])), [stores]);
  if (!notAdded || notAdded.items.length === 0) return null;
  const { items, titles } = notAdded;
  const rest = items.length - LISTED;

  return (
    <Alert variant="destructive" role="alert" className="relative pr-10">
      <AlertTitle>{t('schedules:notAdded.title', { count: items.length })}</AlertTitle>
      <AlertDescription>
        <p>{t('schedules:notAdded.description')}</p>
        <ul
          className="mt-1 flex flex-col gap-1.5"
          aria-label={t('schedules:notAdded.title', { count: items.length })}
        >
          {items.slice(0, LISTED).map((item) => (
            <li key={item.id} className="flex flex-col">
              <span className="text-foreground font-medium">
                {titles[item.id] ?? t('schedules:pool.unknownProduct', { id: item.id })}
              </span>
              {item.code ? <span className="text-xs">{t(`errors:code.${item.code}`)}</span> : null}
              <MemberResultDetail item={item} storeNames={storeNames} />
            </li>
          ))}
        </ul>
        {rest > 0 ? (
          <p className="text-xs">{t('schedules:notAdded.more', { count: rest })}</p>
        ) : null}
      </AlertDescription>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="absolute top-2 right-2 size-7"
        aria-label={t('schedules:notAdded.dismiss')}
        onClick={() => clear(scheduleId)}
      >
        <X aria-hidden />
      </Button>
    </Alert>
  );
}
